import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Command, 
    CommandInput, 
    CommandList, 
    CommandEmpty, 
    CommandGroup, 
    CommandItem,
    CommandDialog
} from '@/components/ui/command';
import { Search, X, Star, Bookmark, Folder, Command as CommandIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
    Sheet, 
    SheetContent, 
    SheetHeader, 
    SheetTitle 
} from '@/components/ui/sheet';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { debounce } from 'lodash';
import axios from 'axios';
import { useMobile } from '../hooks/use-mobile';
import { searchChannels, fetchChannelGroups } from '../services/channel.service';

const ChannelSearch = () => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [channels, setChannels] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const mobileScreen = useMobile();
    const cancelTokenRef = useRef(null);
    
    // Detect if Mac for keyboard shortcut display
    const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    // Fetch groups when modal opens (only once)
    useEffect(() => {
        if (open && groups.length === 0) {
            fetchChannelGroups()
                .then(data => setGroups(data))
                .catch(err => console.error('Failed to fetch groups:', err));
        }
    }, [open, groups.length]);

    // Create debounced search function using lodash
    const debouncedSearch = useMemo(
        () => debounce(async (query) => {
            // Cancel previous request if any
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('New search query');
            }

            // If search query is empty, clear results
            if (!query || !query.trim()) {
                setChannels([]);
                setIsLoading(false);
                return;
            }

            // Set loading state
            setIsLoading(true);

            // Create cancel token for this request
            const CancelToken = axios.CancelToken;
            cancelTokenRef.current = CancelToken.source();

            try {
                const data = await searchChannels(query.trim(), true, cancelTokenRef.current);
                setChannels(data);
                setIsLoading(false);
            } catch (err) {
                if (axios.isCancel(err)) {
                    // Request was cancelled, ignore
                    return;
                }
                console.error('Failed to search channels:', err);
                setChannels([]);
                setIsLoading(false);
            }
        }, 300),
        []
    );

    // Call debounced search when searchQuery changes
    useEffect(() => {
        debouncedSearch(searchQuery);

        // Cleanup: cancel pending requests and cancel debounced function
        return () => {
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Component unmounted or query changed');
            }
            debouncedSearch.cancel();
        };
    }, [searchQuery, debouncedSearch]);

    // Keyboard shortcut: Cmd/Ctrl + K
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(prev => !prev);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Get group name helper
    const getGroupName = useCallback((groupId) => {
        const group = groups.find(g => g.id === groupId);
        return group?.name || null;
    }, [groups]);

    // Categorize channels (backend already returns sorted results)
    const categorizedChannels = useMemo(() => {
        const starred = channels.filter(ch => ch.is_favorited && !ch.is_archived);
        const bookmarked = channels.filter(ch => ch.is_bookmarked && !ch.is_favorited && !ch.is_archived);
        const regular = channels.filter(ch => !ch.is_favorited && !ch.is_bookmarked && !ch.is_archived);
        const archived = channels.filter(ch => ch.is_archived);
        return { starred, bookmarked, regular, archived };
    }, [channels]);

    const handleSelect = useCallback((channelId, isPersonalized) => {
        const url = isPersonalized 
            ? `/channels/${channelId}?tab=personalized`
            : `/channels/${channelId}`;
        navigate(url);
        setOpen(false);
        setSearchQuery('');
    }, [navigate]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSearchQuery('');
        setChannels([]);
        setIsLoading(false);
    }, []);

    const renderChannelItem = (channel) => {
        const groupName = getGroupName(channel.group_id);
        return (
            <CommandItem
                key={channel.id}
                value={`${channel.name} ${groupName || ''}`}
                onSelect={() => handleSelect(channel.id, channel.is_personalized)}
                className="flex items-center gap-2 py-2 cursor-pointer"
            >
                <span className="text-primary font-bold">#</span>
                <span className="flex-1 truncate">{channel.name}</span>
                {groupName && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Folder className="size-3" />
                        {groupName}
                    </span>
                )}
                {channel.is_favorited && <Star className="size-3.5 text-amber-400 fill-amber-400" />}
                {channel.is_bookmarked && <Bookmark className="size-3.5 text-blue-400 fill-blue-400" />}
            </CommandItem>
        );
    };

    const renderChannelList = () => {
        if (isLoading) {
            return (
                <div className="py-8 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                </div>
            );
        }

        if (searchQuery.trim() && channels.length === 0) {
            return (
                <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
                    No channels found
                </CommandEmpty>
            );
        }

        if (!searchQuery.trim()) {
            return (
                <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
                    Start typing to search channels...
                </CommandEmpty>
            );
        }

        return (
            <>
                {categorizedChannels.starred.length > 0 && (
                    <CommandGroup heading="⭐ Starred">
                        {categorizedChannels.starred.map(renderChannelItem)}
                    </CommandGroup>
                )}

                {categorizedChannels.bookmarked.length > 0 && (
                    <CommandGroup heading="🔖 Bookmarked">
                        {categorizedChannels.bookmarked.map(renderChannelItem)}
                    </CommandGroup>
                )}

                {categorizedChannels.regular.length > 0 && (
                    <CommandGroup heading="# Channels">
                        {categorizedChannels.regular.map(renderChannelItem)}
                    </CommandGroup>
                )}

                {categorizedChannels.archived.length > 0 && (
                    <CommandGroup heading="📦 Archived">
                        {categorizedChannels.archived.map(renderChannelItem)}
                    </CommandGroup>
                )}
            </>
        );
    };

    // Mobile
    if (mobileScreen) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setOpen(true)}
                            className="text-foreground hover:bg-accent hover:text-accent-foreground"
                        >
                            <Search className="size-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Search channels</TooltipContent>
                </Tooltip>

                <Sheet open={open} onOpenChange={(open) => !open && handleClose()}>
                    <SheetContent 
                        side="bottom" 
                        className="h-[80vh] rounded-t-xl p-0 flex flex-col"
                        hideCloseButton
                    >
                        <SheetHeader className="px-4 pt-4 pb-2 border-b border-border/30 shrink-0">
                            <div className="flex items-center justify-between">
                                <SheetTitle className="text-lg font-semibold">Search Channels</SheetTitle>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={handleClose}
                                    className="text-foreground hover:bg-accent hover:text-accent-foreground"
                                >
                                    <X className="size-5" />
                                </Button>
                            </div>
                        </SheetHeader>
                        <div className="flex-1 overflow-hidden">
                            <Command className="bg-background h-full" shouldFilter={false} onKeyDown={(e) => e.stopPropagation()}>
                                <CommandInput
                                    placeholder="Search channels..."
                                    value={searchQuery}
                                    onValueChange={setSearchQuery}
                                    autoFocus
                                />
                                <CommandList className="max-h-none overflow-y-auto">
                                    {renderChannelList()}
                                </CommandList>
                            </Command>
                        </div>
                    </SheetContent>
                </Sheet>
            </TooltipProvider>
        );
    }

    // Desktop
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={() => setOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-accent text-muted-foreground hover:text-accent-foreground text-sm transition-all cursor-pointer"
                    >
                        <Search className="size-4" />
                        <span className="hidden lg:inline">Search channels...</span>
                        <kbd className="hidden lg:inline-flex items-center gap-0.5 ml-2 px-1.5 py-0.5 rounded bg-secondary text-xs font-medium text-secondary-foreground">
                            {isMac ? <CommandIcon className="size-3" /> : <span>Ctrl</span>}
                            <span>K</span>
                        </kbd>
                    </button>
                </TooltipTrigger>
                <TooltipContent>
                    Search channels ({isMac ? '⌘' : 'Ctrl+'}K)
                </TooltipContent>
            </Tooltip>

            <CommandDialog 
                open={open} 
                onOpenChange={setOpen}
                title="Search Channels"
                description="Search and navigate to any channel"
                showCloseButton={false}
            >
                <Command 
                    shouldFilter={false}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <CommandInput
                        placeholder="Search channels..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <CommandList>{renderChannelList()}</CommandList>
                </Command>
            </CommandDialog>
        </TooltipProvider>
    );
};

export default ChannelSearch;
